import React from 'react';
import { Subtract } from 'utility-types';

import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { Projects } from '../../projects/resources/Projects';
import { Filter } from '../../ts/helpers/Filter';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { IPublishedConnectionDetailInfo, IPublishedConnectionInfo } from '../models/IPublishedConnectionInfo';
import { PublishedConnections } from '../resources/PublishedConnections';
import { IPublishedConnectionActions } from '../serverApi/types/IPublishedConnectionActions';

interface InjectedPublishedConnectionSpaceData {
  readonly publishedConnectionAllowedActions: IPublishedConnectionActions;
  readonly publishedConnectionDetailInfo: IPublishedConnectionDetailInfo;
  readonly publishedConnectionInfo: IPublishedConnectionInfo;
  readonly projectHierarchy: INamedItemInfo[];
}

export interface InjectedPublishedConnectionSpaceDataFetcherProps extends InjectedPublishedConnectionSpaceData {
  readonly refreshData: () => void;
}

export interface PublishedConnectionSpaceDataFetcherProps {
  readonly siteName: string;
  readonly publishedConnectionId: string;
  readonly siteUrls: SpacesSiteUrls;
  readonly isRequestAccessEnabled: boolean;
}

export function withPublishedConnectionSpaceDataFetcher<P extends InjectedPublishedConnectionSpaceDataFetcherProps>(
  Component: React.ComponentType<P>
) {
  // TODO - achristian - TFSID:1059566 - 2020-02-03 - Redo this component with less side-effects.
  return function PublishedConnectionSpaceDataFetcher(
    props: Subtract<P, InjectedPublishedConnectionSpaceDataFetcherProps> & PublishedConnectionSpaceDataFetcherProps
  ) {
    const [ error, setError ] = React.useState(false);
    const [ injectedData, setInjectedData ] = React.useState<InjectedPublishedConnectionSpaceData>(undefined);
    const [ errorCode, setErrorCode ] = React.useState(null);

    let didCancel = false;

    const refreshData = () => {
      fetchPublishedConnectionSpaceData(props.publishedConnectionId)
        .then(injectedData => {
          !didCancel && setError(false);
          !didCancel && setInjectedData(injectedData);
        })
        .catch((e) => {
          !didCancel && setError(true);
          !didCancel && setErrorCode(e);
        });
    };

    React.useEffect(() => {
      refreshData();
      // This noncompliance existed before we enabled the lint rule. If you’re here, please consider fixing it!
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return () => didCancel = true;
    }, [ props.publishedConnectionId, props.siteName ]);

    if (error) {
      if (props.isRequestAccessEnabled) {
        return (
          <RequestAccessError
            url={props.siteUrls.exploreUrl()}
            contentIdentifier={props.publishedConnectionId}
            contentType={'virtualConnection'}
            errorCode={errorCode}
          />
        );
      }
      return <RouterError errorType={'virtualConnection'} urls={props.siteUrls}/>;
    } else if (!injectedData) {
      return null;
    }

    return <Component {...props as unknown as P} {...injectedData} refreshData={refreshData}/>;
  };
}

async function fetchPublishedConnectionSpaceData(publishedConnectionId: string): Promise<InjectedPublishedConnectionSpaceData> {
  const publishedConnectionAllowedActions = await PublishedConnections.actions(publishedConnectionId);
  const publishedConnectionDetailInfo = await PublishedConnections.getById(publishedConnectionId);
  const publishedConnectionInfo = await PublishedConnections.all()
    .withFilters([Filter.fieldEq('id', publishedConnectionId)])
    .getFirst();
  const projectHierarchy = await Projects.getProjectHierarchy(publishedConnectionDetailInfo.project.id);

  return {
    publishedConnectionAllowedActions,
    publishedConnectionDetailInfo,
    publishedConnectionInfo,
    projectHierarchy,
  };
}
